import React, {useEffect, useState} from "react";
import {useAuthInfo} from "@propelauth/react";
import {FileText, Maximize2, Sparkles, Table} from "lucide-react";
import {fetchWithToken, postWithToken, putWithToken} from "../../api/apiBase";
import {Button} from "../ui/button";
import {Spinner} from "../ui/spinner";
import {Textarea} from "../ui/textarea";
import {Badge} from "../ui/badge";
import {useToast} from "../ui/use-toast";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../ui/dialog";
import {moneyFormatter} from "../../lib/formatters";
import {BidLevelingState, SCOPE_STATUS, ScopeItem} from "../developer/ai_leveling";
import {LevelingDetails, levelingCounts} from "../developer/LevelingDetails";
import {PlugsView} from "../bid/bidding_model";
import {ReviewDocuments} from "./ReviewDocuments";
import {SplitPanes} from "../ui/splitPanes";


// server: backend/feedback/smart_feedback_v3.py
interface BidReview {
    summary: string;
    scope_gaps: string[];          // in the scope documents, not covered by the bid
    form_issues: string[];
    mismatches: string[];
    exclusions_qualifications: string[];
    feedback: string;
    // the bid's digest - AI leveling compares other bids with these. older reviews don't have them
    scope_items?: ScopeItem[];
    approach?: string[];
}

interface SmartFeedbackV3 {
    id: string;
    bid_id: string;
    status: "running" | "done" | "failed" | "shared";
    error: string | null;
    started_at: string;
    finished_at: string | null;
    file_ids: string[];
    skipped_files: string[];       // '<source>: <name>' - not reviewed (not pdf / docx, or unreadable)
    review: BidReview | null;
    edited: string | null;         // developer's version of review.feedback
    shared_at: string | null;
    shared_by: string | null;
    new_files: number;             // documents added after the review was made
}


interface SmartFeedbackRowProps {
    project_id: string;
    bid_id: string;
    // AI leveling against the other bids - same row, one generate for both. owned by BidPage (the SOV shows its plugs)
    leveling: BidLevelingState;
    onPlugsChanged: (view: PlugsView) => void;
    // the popup's other side - the bid's SOV with its plugs and the suggestions (BidPage builds it)
    sov: React.ReactNode;
    // eg the bidder's name - in the popup's title
    title?: string;
    // the popup's documents pane - the bid's and the scope's files
    bidFolderId?: string;
    scopeId?: string | null;
}

function ReviewList({title, items}: { title: string, items: string[] }) {
    if (!items || items.length === 0) return null;
    return <div className="mb-2">
        <div className="font-semibold">{title}</div>
        <ul className="list-disc ml-5">
            {items.map((x, i) => <li key={i}>{x}</li>)}
        </ul>
    </div>
}

function ScopeItemList({items}: { items?: ScopeItem[] }) {
    if (!items || items.length === 0) return null;
    return <div className="mb-2">
        <div className="font-semibold">Scope items</div>
        <ul className="ml-1">
            {items.map((x, i) => <li key={i} className="flex gap-1 items-baseline">
                <span className={"rounded px-1 shrink-0 " + SCOPE_STATUS[x.status].style}>{SCOPE_STATUS[x.status].label}</span>
                <span>
                    {x.item}
                    {x.amount !== null && ` · ${moneyFormatter.format(x.amount)}`}
                    {x.note && <span className="text-muted-foreground"> - {x.note}</span>}
                </span>
            </li>)}
        </ul>
    </div>
}

// shows / hides one of the popup's columns
function PaneButton({on, disabled, onClick, children}: {
    on: boolean, disabled?: boolean, onClick: () => void, children: React.ReactNode
}) {
    return <Button variant={on ? "secondary" : "outline"} size="sm" disabled={disabled} onClick={onClick}>
        {children}
    </Button>
}

/**
 * Developer only - AI review of a submitted bid, in two parts:
 *   the bid on its own (scope + bid documents, bid form) - smart feedback v3. Generated in the background when
 *     the bid is submitted. The developer edits the message, then shares it - it goes to the bidder as a message
 *     in the bid conversation.
 *   the bid against the other bids on the scope - AI leveling (LevelingDetails). developer only, never shared.
 * One Generate does both: the server reviews the bid first if it has no review, then levels it.
 * The row shows status; Review opens a full page popup - the documents (ReviewDocuments), the review when there is
 * one, and the SOV with plugs, in resizable columns.
 * A row of the DeveloperBidCard.
 * */
export function SmartFeedbackRow({project_id, bid_id, leveling, onPlugsChanged, sov, title, bidFolderId, scopeId}: SmartFeedbackRowProps) {
    const {accessToken} = useAuthInfo();
    const {toast} = useToast();
    const [doc, setDoc] = useState<SmartFeedbackV3 | null>(null);
    const [loaded, setLoaded] = useState<boolean>(false);
    // the message - what is in the box vs what the server has
    const [text, setText] = useState<string>("");
    const [savedText, setSavedText] = useState<string>("");
    // the review details open in a full page popup, beside the SOV
    const [open, setOpen] = useState<boolean>(false);
    // the popup's columns, left to right - the documents to check the review against, the AI review, the SOV
    const [shown, setShown] = useState({docs: true, review: true, sov: true});

    const url = `/projects/${project_id}/smart_feedback_v3/${bid_id}`;

    const show = (res: SmartFeedbackV3 | null) => {
        setDoc(res);
        const message = res ? (res.edited ?? res.review?.feedback ?? "") : "";
        setText(message);
        setSavedText(message);
    }

    const onError = (error: any) => {
        console.error("smart feedback:", error);
        toast({
            title: "Error",
            description: error?.message || "Something went wrong with the bid review.",
            variant: "destructive",
        });
    }

    useEffect(() => {
        fetchWithToken(url, accessToken as string)
            .then(show)
            .catch(onError)
            .finally(() => setLoaded(true));
    }, [url]);

    // poll while the review runs in the background
    useEffect(() => {
        if (doc?.status !== "running") return;
        const timer = setTimeout(() => {
            fetchWithToken(url, accessToken as string).then(show).catch(onError);
        }, 5000);
        return () => clearTimeout(timer);
    }, [doc]);

    const onGenerate = () => {
        postWithToken(`${url}/generate`, JSON.stringify({}), accessToken as string)
            .then(show)
            .catch(onError);
    }

    const saveText = async () => {
        if (text === savedText) return;
        const res = await putWithToken(url, JSON.stringify({edited: text}), accessToken as string);
        setDoc(res);
        setSavedText(text);
    }

    const onShare = () => {
        // the server shares what it has - save the box first
        saveText()
            .then(() => postWithToken(`${url}/share`, JSON.stringify({}), accessToken as string))
            .then((res) => {
                show(res);
                toast({title: "Shared", description: "Feedback sent to the bidder.", variant: "default"});
            })
            .catch(onError);
    }

    // the one generate - the server reviews the bid first if it has no review (or it failed), then compares
    // it with the other bids. the review may be running now too - fetch it
    const onGenerateBoth = () => {
        leveling.generate()
            .then(() => fetchWithToken(url, accessToken as string))
            .then(show)
            .catch(onError);
    }

    const status = doc?.status;
    const review = doc?.review;
    const hasReview = (status === "done" || status === "shared") && !!review;
    const lev = leveling.doc;
    const levStatus = lev?.status;
    const hasLeveling = levStatus === "done" && !!lev?.result;
    const allLoaded = loaded && leveling.loaded;
    // the popup's review column needs something to show
    const canReview = hasReview || hasLeveling;

    // a popup column on / off - but never all of them
    const toggle = (key: "docs" | "review" | "sov") => {
        const next = {...shown, [key]: !shown[key]};
        if (next.docs || (next.review && canReview) || next.sov) setShown(next);
    }

    // shown in the header while closed, eg "8 scope gaps · 3 form issues · 2 suggested plugs"
    const counts = [
        review ? ([
            [review.scope_gaps?.length, "scope gaps"],
            [review.form_issues.length, "form issues"],
            [review.mismatches.length, "mismatches"],
        ] as [number, string][]).filter(([n]) => n > 0).map(([n, label]) => `${n} ${label}`).join(" · ") : "",
        levelingCounts(leveling),
    ].filter((x) => x).join(" · ");

    // what is running / failed / not run yet - in the row, and on top of the popup's review side
    const statusLines = <>
        {!allLoaded && <Spinner/>}

        {allLoaded && doc === null && lev === null &&
            <div className="flex items-center justify-between">
                <span className="text-muted-foreground">
                    No review yet - reviews the bid's documents, and compares it with the other bids on the scope.
                </span>
                <Button variant="outline" size="sm" onClick={onGenerateBoth}>Generate</Button>
            </div>
        }

        {status === "running" &&
            <div className="flex items-center gap-2">
                <Spinner/> Reviewing the bid documents and form...
            </div>
        }
        {levStatus === "running" &&
            <div className="flex items-center gap-2">
                <Spinner/> Comparing with the other bids on the scope...
            </div>
        }

        {status === "failed" &&
            <div className="flex items-center justify-between">
                <span className="text-destructive">Review failed: {doc?.error}</span>
                <Button variant="outline" size="sm" onClick={onGenerateBoth}>Try again</Button>
            </div>
        }
        {levStatus === "failed" &&
            <div className="flex items-center justify-between">
                <span className="text-destructive">Comparison failed: {lev?.error}</span>
                <Button variant="outline" size="sm" onClick={onGenerateBoth}>Try again</Button>
            </div>
        }

        {/* reviewed on submit, never compared - same generate, the review is kept */}
        {allLoaded && hasReview && lev === null &&
            <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Not compared with the other bids on the scope yet.</span>
                <Button variant="outline" size="sm" onClick={onGenerateBoth}>Compare</Button>
            </div>
        }

        {hasLeveling && lev?.stale &&
            <div className="text-amber-600 mb-2">
                Bids on this scope changed since the comparison ran - rerun it to include them.
            </div>}
    </>;

    // the popup's AI review column - left out when there is no review or comparison to show
    const reviewPane = <div className="h-full overflow-y-auto pr-2 text-xs">
        {statusLines}

        {hasReview && review &&
            <>
                <div className="mb-2">{review.summary}</div>
                <ReviewList title="Scope gaps" items={review.scope_gaps}/>
                <ReviewList title="Bid form issues" items={review.form_issues}/>
                <ReviewList title="Form vs documents" items={review.mismatches}/>
                <ReviewList title="Exclusions & qualifications" items={review.exclusions_qualifications}/>
                <ScopeItemList items={review.scope_items}/>
                <ReviewList title="Approach" items={review.approach ?? []}/>

                {doc.file_ids.length === 0 &&
                    <div className="text-muted-foreground mb-2">No documents - reviewed the bid form only.</div>}
                {doc.skipped_files.length > 0 &&
                    <div className="text-muted-foreground mb-2">Not reviewed (only pdf and docx are read): {doc.skipped_files.join(", ")}</div>}
                {doc.new_files > 0 && status === "done" &&
                    <div className="text-amber-600 mb-2">
                        {doc.new_files} document(s) were added after this review - regenerate to include them.
                    </div>}
            </>
        }

        {/* against the other bids - developer only, never in the message */}
        <LevelingDetails leveling={leveling} onPlugsChanged={onPlugsChanged}/>

        {hasReview && review &&
            <>
                <div className="font-semibold mt-3 mb-1">Message to bidder</div>
                <Textarea
                    className="min-h-[120px] text-xs"
                    value={text}
                    disabled={status === "shared"}
                    onChange={(e) => setText(e.target.value)}
                    onBlur={() => saveText().catch(onError)}
                />

                {status === "shared" ?
                    <div className="text-muted-foreground mt-2">
                        Shared {doc.shared_at ? new Date(doc.shared_at).toLocaleString() : ""} by {doc.shared_by}
                    </div>
                    :
                    <div className="flex justify-end gap-2 mt-2">
                        <Button variant="outline" size="sm" onClick={onGenerate}>Regenerate review</Button>
                        <Dialog>
                            <DialogTrigger asChild>
                                <Button size="sm" disabled={!text.trim()}>Share with bidder</Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-md">
                                <DialogHeader>
                                    <DialogTitle>Share feedback</DialogTitle>
                                    <DialogDescription>
                                        The message is sent to the bidder in the bid conversation.
                                        It cannot be edited after it is shared.
                                    </DialogDescription>
                                </DialogHeader>
                                <DialogFooter className="sm:justify-start">
                                    <DialogClose asChild>
                                        <Button type="button" onClick={onShare}>Share</Button>
                                    </DialogClose>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                }
            </>
        }
    </div>;

    return <div className="text-xs">
        <div className="flex items-center gap-2 text-sm font-medium mb-2">
            AI review
            {status && <Badge variant="outline">{status}</Badge>}
            {levStatus && levStatus !== "done" && <Badge variant="outline">comparison {levStatus}</Badge>}
            {lev?.stale && <Badge variant="outline" className="text-amber-600">stale</Badge>}
            <span className="text-xs font-normal text-muted-foreground">{counts}</span>
            {/* the documents and the SOV are worth a look without an AI review too */}
            <Button variant="outline" size="sm" className="ml-auto" onClick={() => setOpen(true)}>
                <Maximize2 className="w-3 h-3 mr-1"/> Review
            </Button>
        </div>
        {statusLines}

        {/* the way to review: the documents, the AI review when there is one, and the SOV with its plugs and
            suggestions - side by side, resizable */}
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-[98vw] w-[98vw] h-[95vh] p-4 flex flex-col gap-2">
                {/* title | column buttons, centered | (the dialog's close X) */}
                <DialogHeader className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 space-y-0">
                    <DialogTitle>Review{title ? ` - ${title}` : ""}</DialogTitle>
                    <div className="flex gap-2">
                        <PaneButton on={shown.docs} onClick={() => toggle("docs")}>
                            <FileText className="w-3 h-3 mr-1"/> Documents
                        </PaneButton>
                        <PaneButton on={shown.review && canReview} disabled={!canReview} onClick={() => toggle("review")}>
                            <Sparkles className="w-3 h-3 mr-1"/> Review
                        </PaneButton>
                        <PaneButton on={shown.sov} onClick={() => toggle("sov")}>
                            <Table className="w-3 h-3 mr-1"/> SOV
                        </PaneButton>
                    </div>
                </DialogHeader>
                <SplitPanes panes={[
                    shown.docs && {
                        key: "docs", size: 35, node:
                            <ReviewDocuments project_id={project_id} bidFolderId={bidFolderId} scopeId={scopeId}
                                             onClose={() => toggle("docs")}/>
                    },
                    shown.review && canReview && {key: "review", size: 30, node: reviewPane},
                    shown.sov && {key: "sov", size: 35, node: <div className="h-full overflow-auto">{sov}</div>},
                ]}/>
            </DialogContent>
        </Dialog>
    </div>
}
